import { BehaviorSubject, combineLatest, interval, Observable, Subscription } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import RawRequestMiddleware from '@/repositories/api/RawRequestMiddleware';
import RawResponseMiddleware from '@/repositories/api/RawResponseMiddleware';
import { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import User from '@/repositories/data/User';
import UserRepository from '@/repositories/UserRepository';
import Resource from '@/repositories/Resource';

export default class GlobalViewModel implements RawRequestMiddleware, RawResponseMiddleware {
    private readonly _userRepository: UserRepository;
    private readonly _loginEndpoint: string;
    private readonly _currentUser: Observable<Resource<User>>;
    private readonly mobileFullscreenModalOpenSubject: BehaviorSubject<boolean>;
    private readonly lightBoxOpenSubject: BehaviorSubject<boolean>;
    private readonly errorActiveSubject: BehaviorSubject<boolean>;
    private readonly warnGuestActiveSubject: BehaviorSubject<boolean>;
    private baseClippedSubscription: Subscription;
    private keepAliveSubscription: Subscription;
    private reportedUserIsGuest = false;

    constructor(userRepository: UserRepository, loginEndpoint: string) {
        this._userRepository = userRepository;
        this._loginEndpoint = loginEndpoint;
        this.errorActiveSubject = new BehaviorSubject<boolean>(false);
        this.warnGuestActiveSubject = new BehaviorSubject<boolean>(false);
        this.mobileFullscreenModalOpenSubject = new BehaviorSubject<boolean>(false);
        this.lightBoxOpenSubject = new BehaviorSubject<boolean>(false);
        this.baseClippedSubscription = combineLatest([this.mobileFullscreenModalOpenSubject, this.lightBoxOpenSubject])
            .pipe(map(([mobileFullscreen, lightBox]) => mobileFullscreen || lightBox))
            .subscribe(active => {
                const html = document.getElementById('base');
                if (html) {
                    if (active) {
                        html.classList.add('is-clipped');
                    } else {
                        html.classList.remove('is-clipped');
                    }
                }
            });
        this._currentUser = this._userRepository.findCurrent().pipe(
            tap(userResource => {
                if (userResource.isSuccess) {
                    if (!this.reportedUserIsGuest && !userResource.data) {
                        this.reportedUserIsGuest = true;
                        this.warnGuestActiveSubject.next(true);
                    }
                }
            }),
        );
        this.keepAliveSubscription = interval(10 * 60 * 1000).subscribe(() => this._userRepository.keepAlive());
    }

    public beginUserLogin(): void {
        window.location.href = this._loginEndpoint;
    }

    public beginUserLogout(): void {
        this._userRepository.logout().subscribe(resource => {
            if (resource.isSuccess && resource.data) {
                window.location.href = resource.data.logoutUrl;
            }
        });
    }

    public get userObservable(): Observable<Resource<User>> {
        return this._currentUser;
    }

    get verifiedUser(): Observable<User | null> {
        return this.userObservable.pipe(
            filter(t => !t.isLoading),
            map(t => (t.isSuccess && t.data ? t.data : null)),
        );
    }

    get isErrorActive(): Observable<boolean> {
        return this.errorActiveSubject.asObservable();
    }

    public reportError(): void {
        this.errorActiveSubject.next(true);
    }

    public dismissError(): void {
        this.errorActiveSubject.next(false);
    }

    public beforeRequestInterceptor():
        | ((request: InternalAxiosRequestConfig) => InternalAxiosRequestConfig)
        | undefined {
        return config => {
            this.dismissError();
            return config;
        };
    }

    public dataInterceptor(): ((value: AxiosResponse<any>) => AxiosResponse<any>) | undefined {
        return value => {
            if (value.data.errors) {
                this.reportError();
            }
            return value;
        };
    }

    public errorInterceptor(): ((error: any) => any) | undefined {
        return error => {
            if (error.response && error.response.status === 401) {
                // TODO: review this
                this.beginUserLogin();
            } else if (!error.response || error.response.status >= 500) {
                this.reportError();
            }
            return Promise.reject(error);
        };
    }

    public get isMobileFullscreenModalOpen(): boolean {
        return this.mobileFullscreenModalOpenSubject.getValue();
    }

    public set isMobileFullscreenModalOpen(value: boolean) {
        this.mobileFullscreenModalOpenSubject.next(value);
    }

    public get isLightBoxOpen(): boolean {
        return this.lightBoxOpenSubject.getValue();
    }

    public set isLightBoxOpen(value: boolean) {
        this.lightBoxOpenSubject.next(value);
    }
}
